/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { ReportSeriesFacetGeneratorEnum } from '../../../../../Common/Reports/models/report-series-facet-generator.enum';
import { MADSummarySeriesTypesEnum } from '../../../../../Monitor/Shared/enums/mad-series-types.enum';
import { IndividualReportSeriesConfig } from '../../../../../Monitor/Core/services/monitoring-dashboard-search.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { ReportsNeoFacetKeys } from '../../../../../Common/Reports/enums/reports-neo-facet-keys.enum';
import { getSentimentMapper } from '../../../../../Common/Reports/components/ChartDrillDownDialog/chart-drill-down-dialog.component';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ReportsSeriesMapperService } from '../../../../../Common/Reports/services/reports-series-mapper.service';
import { MentionsSearchResponseModel } from '../../../../../api/mentions/model/mentions-search-response.model';
import { AggregateDateFacetTypesEnum } from '@api/mentions/enum/aggregate-date-facet-types.enum';
import { DateFacetTypesEnum } from '@api/mentions/enum/date-facet-types.enum';
import { NeoFacetRequestApiModel } from '@api/mentions/model/neo-facet-request-api.model';
import { NeoFacetDateFieldApiEnum } from '@api/mentions/model/neo-facet-date-field-api.enum';
import { NeoFacetTermFieldApiEnum } from '@api/mentions/enum/neo-facet-term-field-api.enum';
import { BucketFunctionApiEnum } from '@api/mentions/enum/bucket-function-api.enum';
import { DataPointModel } from '@Common/Charts/models/data-point.model';
import { ReportsSeriesDataMapperService } from '@Common/Reports/services/reports-series-data-mapper.service';
import { ChartMetricsEnum } from '@Common/Reports/enums/chart-metrics.enum';
import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';
import { MentionSearchFacetTypesEnum } from '@api/mentions/enum/mention-search-facet-types.enum';
import { mediaTypeToPieMap } from '@Common/Reports/services/reports-series-mapper.service';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { SocialSeriesNeoFacetKeysEnum } from '@Social/Intelligence/enums/social-series-neo-facet-keys.enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { Mention } from '@Common/Mentions/models/mention';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { MADChartsService,
    MADMapChartCountryNeoFacetRequest
} from '@Monitor/Shared/services/mad-charts.service';
import { topContactsNeoFacetRequest, topOutletsNeoFacetRequest } from '@Common/Reports/models/report-neo-facet-requests.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';

@Injectable()
export class MADSummaryService extends MADChartsService {

    readonly dateBulletFormat: string = 'YYYY/MM/DD hh:mm';
    readonly MADSummarySeriesConfig: IndividualReportSeriesConfig[] = [
        {
            type: MADSummarySeriesTypesEnum.totalVolumeCard,
            label: `Monitor.MAD.summary.${MADSummarySeriesTypesEnum.totalVolumeCard}.label`,
            facetGenerator: () => {
                return {
                    facet: summaryTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + (bucket.count || 0), 0);
                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSummarySeriesTypesEnum.totalVolumeCardPreviousPeriod,
            label: `Monitor.MAD.summary.${MADSummarySeriesTypesEnum.totalVolumeCard}.label`,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: summaryTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + (bucket.count || 0), 0);
                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSummarySeriesTypesEnum.totalImpressionCard,
            label: `Monitor.MAD.summary.${MADSummarySeriesTypesEnum.totalImpressionCard}.label`,
            facetGenerator: () => {
                return {
                    facet: summaryTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSummarySeriesTypesEnum.totalImpressionCardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: summaryTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSummarySeriesTypesEnum.totalAVECard,
            label: `Monitor.MAD.summary.${MADSummarySeriesTypesEnum.totalAVECard}.label`,
            facetGenerator: () => {
                return {
                    facet: summaryTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSummarySeriesTypesEnum.totalAVECardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: summaryTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSummarySeriesTypesEnum.summaryTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave],
            facetGenerator: () => {
                const facetRequest = JSON.parse(JSON.stringify(summaryTrendNeoFacetRequest));
                return {
                    facet: facetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapDateDataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSummarySeriesTypesEnum.positive,
            label: `Monitor.MAD.summary.${MADSummarySeriesTypesEnum.positive}.label`,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.positive)
        },
        {
            type: MADSummarySeriesTypesEnum.positivePreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.positive)
        },
        {
            type: MADSummarySeriesTypesEnum.negative,
            label: `Monitor.MAD.summary.${MADSummarySeriesTypesEnum.negative}.label`,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.negative)
        },
        {
            type: MADSummarySeriesTypesEnum.negativePreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: [MentionSearchFacetTypesEnum.sentiment],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper: getSentimentMapper(SearchCriteriaQuerySentimentsEnum.negative)
        },
        {
            type: MADSummarySeriesTypesEnum.averageDomainAuthority,
            label: `Monitor.MAD.summary.${MADSummarySeriesTypesEnum.averageDomainAuthority}.label`,
            facetGenerator: () => {
                return {
                    facet: {
                        field: DateFacetTypesEnum.created,
                        sliceSizeInSecond: 86400,
                        aggregateFacets: [
                            AggregateDateFacetTypesEnum.SUM_DOMAIN_AUTHORITY
                        ]
                    },
                    identifier: ReportSeriesFacetGeneratorEnum.dateFacet
                };
            },
            dataMapper: (response) => {
                return this.getAverageDomainAuthorityFromResponse(response);
            }
        },
        {
            type: MADSummarySeriesTypesEnum.averageDomainAuthorityPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: {
                        field: DateFacetTypesEnum.created,
                        sliceSizeInSecond: 86400, // 10000 days, arbitrarily big so that backend can return one slice
                        aggregateFacets: [
                            AggregateDateFacetTypesEnum.SUM_DOMAIN_AUTHORITY
                        ]
                    },
                    identifier: ReportSeriesFacetGeneratorEnum.dateFacet
                };
            },
            dataMapper: (response) => {
                return this.getAverageDomainAuthorityFromResponse(response);
            }
        },
        {
            type: MADSummarySeriesTypesEnum.map,
            metric: ChartMetricsEnum.volumeOfMentions,
            facetGenerator: () => MADMapChartCountryNeoFacetRequest,
            asyncDataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapLocationDataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSummarySeriesTypesEnum.mediaTypeBreakdown,
            facetGenerator: () => {
                return {
                    facet: mediaTypeBreakdownNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const mediaFacetResults = response.neoFacetResults.find(facet => facet.key === NeoFacetTermFieldApiEnum.MEDIA_TYPE)?.buckets;
                const data: DataPointModel[] = [];
                const mediaTypeBreakdownDataMap: Map<MediaTypeEnum, DataPointModel> = new Map();
                if (mediaFacetResults) {
                    for (const facetValue of mediaFacetResults) {
                        if (!facetValue.count) {
                            continue;
                        }
                        let dataPoint: DataPointModel;
                        let count;
                        const mediaType = facetValue.termValue as MediaTypeEnum;
                        const mapping = mediaTypeToPieMap.get(mediaType);

                        if (mapping) {
                            const s = series.clone();
                            const filterOverrides = series.mentions.filters.searchCriteria.clone();
                            filterOverrides.query.includedMediaTypes = [ new MediaTypeModel(mediaType) ];
                            switch (series.metric) {
                                case ChartMetricsEnum.VOLUME:
                                    count = facetValue.count;
                                    break;
                                case ChartMetricsEnum.IMPRESSIONS:
                                    count = facetValue.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count;
                                    break;
                                case ChartMetricsEnum.AVE:
                                    count = facetValue.buckets[NeoFacetTermFieldApiEnum.AD_VALUE_USD][0].count;
                                    break;

                            }
                            dataPoint = {
                                label: this.localizationService.transform(mapping.label),
                                mappingKeys: {
                                    source: series.label,
                                    date: series.mentions.isPreviousPeriod ? 0 : 1,
                                    mediaType: mediaType
                                },
                                value: count,
                                valueLabel: this.localizationService.transform(mapping.label),
                                mentionCount: count,
                                viewUrl: s.getSocialSeriesUrl(filterOverrides, series.mentions.isPreviousPeriod, true)
                            };
                            mediaTypeBreakdownDataMap.set(mediaType, dataPoint);
                        }
                    }
                    // This is so that they always appear in the order specified by the MEDIA_TYPE_MAP
                    for (const [key, value] of Array.from(mediaTypeToPieMap)) {
                        if (mediaTypeBreakdownDataMap.get(key)) {
                            data.push(mediaTypeBreakdownDataMap.get(key));
                        }
                    }
                }

                return data;
            }
        },
        {
            type: MADSummarySeriesTypesEnum.topMentions,
            mentionProvider: new BehaviorSubject<Mention[]>(null),
            mentionRequestGenerator: () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: SearchCriteriaSortFieldEnum.audience_reach,
                    pageSize: 5
                };
            },
            mentionDataMapper: (response) => {
                return response.mentions.map(apiMention => new Mention(apiMention));
            }
        },
        {
            type: MADSummarySeriesTypesEnum.topOutlets,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave, ChartMetricsEnum.domainAuthority],
            facetGenerator: () => {
                return {
                    facet: topOutletsNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapTop10DataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSummarySeriesTypesEnum.topAuthors,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ave, ChartMetricsEnum.domainAuthority],
            facetGenerator: () => {
                return {
                    facet: topContactsNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapTop10DataFromNeoFacet(response, series);
            }
        }
    ];

    constructor(
        public localizationService: LocalizationService,
        public reportsSeriesMapperService: ReportsSeriesMapperService,
        public reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        public routingService: MonitoringRoutingService,
        public journodexSearchService: JournodexRegionsService
    ) {
        super(routingService, localizationService, journodexSearchService);
        this.setChartConfig(this.MADSummarySeriesConfig);
    }

    getAverageDomainAuthorityFromResponse(response: MentionsSearchResponseModel): DataPointModel[] {
        const results = response.dateFacetResults?.[0]?.values;
        if (results) {
            let totalDA = 0;
            let mentionCount = 0;
            results.forEach((x, i) => {
                totalDA += (x.aggregateFacets?.[AggregateDateFacetTypesEnum.SUM_DOMAIN_AUTHORITY] || 0);
                mentionCount += x.count;
            });

            return [{
                count: mentionCount > 0 ? totalDA / mentionCount : 0
            }];
        }

        return [{ count: 0 }];
    }
}

const summaryTrendNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.overtimeFacet,
    aggregateBucketRequests: [
        {
            key: ReportsNeoFacetKeys.impressionsOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
        {
            key: ReportsNeoFacetKeys.aveOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        }
    ]
};

const mediaTypeBreakdownNeoFacetRequest: NeoFacetRequestApiModel = {
    termField: NeoFacetTermFieldApiEnum.MEDIA_TYPE,
    key: NeoFacetTermFieldApiEnum.MEDIA_TYPE,
    aggregateBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
        {
            key: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
            field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        }
    ]
};
